// Match Color — match color profile of one image to another.
// Functional approximation: computes color statistics (mean, std) of source
// and target, then shifts/scales target channels to match source.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface MatchColorParams {
  /** Mean luminance of the source image (0..255). Computed from reference. */
  sourceMean: number;
  /** Mean luminance of the target image (0..255). Computed automatically. */
  targetMean: number;
  /** Intensity of the match (0..100). */
  intensity: number;
  /** Fade the effect (0..100). */
  fade: number;
  /** Neutralize color cast (boolean). */
  neutralize: boolean;
  /** Brightness adjustment (-100..100). */
  brightness: number;
  /** Color intensity (-100..100). */
  colorIntensity: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function computeStats(data: Uint8ClampedArray, w: number, h: number) {
  const n = w * h;
  let sumR = 0, sumG = 0, sumB = 0;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    sumR += data[o]!;
    sumG += data[o + 1]!;
    sumB += data[o + 2]!;
  }
  const meanR = sumR / n;
  const meanG = sumG / n;
  const meanB = sumB / n;

  let varR = 0, varG = 0, varB = 0;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    varR += (data[o]! - meanR) ** 2;
    varG += (data[o + 1]! - meanG) ** 2;
    varB += (data[o + 2]! - meanB) ** 2;
  }
  const stdR = Math.sqrt(varR / n);
  const stdG = Math.sqrt(varG / n);
  const stdB = Math.sqrt(varB / n);

  return { meanR, meanG, meanB, stdR, stdG, stdB };
}

export const MatchColor: Filter<MatchColorParams> = {
  id: "match-color",
  displayName: "Match Color",
  defaultParams: {
    sourceMean: 128,
    targetMean: 128,
    intensity: 100,
    fade: 0,
    neutralize: false,
    brightness: 0,
    colorIntensity: 100,
  },

  apply(
    input: PixelBuffer,
    params: MatchColorParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    const targetStats = computeStats(src, w, h);

    // Use sourceMean as a proxy for the source image's mean luminance
    // In a real implementation, this would come from a reference image
    const sourceMean = params.sourceMean;
    const intensity = params.intensity / 100;
    const fade = params.fade / 100;
    const brightnessShift = params.brightness;
    const colorFactor = params.colorIntensity / 100;

    // Compute overall target mean luminance
    const targetLumMean = (targetStats.meanR + targetStats.meanG + targetStats.meanB) / 3;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o]!, g = src[o + 1]!, b = src[o + 2]!;

      // Normalize to zero mean, unit variance
      const nr = targetStats.stdR > 0 ? (r - targetStats.meanR) / targetStats.stdR : 0;
      const ng = targetStats.stdG > 0 ? (g - targetStats.meanG) / targetStats.stdG : 0;
      const nb = targetStats.stdB > 0 ? (b - targetStats.meanB) / targetStats.stdB : 0;

      // Scale to source statistics (using sourceMean as proxy)
      const sourceStd = 64; // typical std for a well-distributed image
      let mr = nr * sourceStd * colorFactor + sourceMean;
      let mg = ng * sourceStd * colorFactor + sourceMean;
      let mb = nb * sourceStd * colorFactor + sourceMean;

      // Neutralize: remove color cast by equalizing channel means
      if (params.neutralize) {
        const avg = (mr + mg + mb) / 3;
        mr = mg = mb = avg;
      }

      // Brightness
      mr += brightnessShift;
      mg += brightnessShift;
      mb += brightnessShift;

      // Blend with original based on intensity and fade
      mr = mr * intensity + r * (1 - intensity);
      mg = mg * intensity + g * (1 - intensity);
      mb = mb * intensity + b * (1 - intensity);

      // Fade
      mr = mr * (1 - fade) + r * fade;
      mg = mg * (1 - fade) + g * fade;
      mb = mb * (1 - fade) + b * fade;

      dst[o] = clamp8(Math.round(mr));
      dst[o + 1] = clamp8(Math.round(mg));
      dst[o + 2] = clamp8(Math.round(mb));
      dst[o + 3] = src[o + 3]!;
    }

    return out;
  },
};
